import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  LockOpen as LockOpenIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import CommonModal from "../../components/CommonModal";
import api from "../../api";

const STATUS_TABS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const TYPE_LABELS = { past_paper: "Past paper", mock_exam: "Mock exam" };

const STATUS_CHIP = {
  pending: { label: "Pending", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "error" },
};

const formatDate = (value) => (value ? new Date(value.replace(" ", "T")).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—");

/**
 * Tutor requests for past paper / mock exam PDFs. Admin approves or rejects them here, revokes
 * approved access, or grants access directly. Items already assigned to a tutor's students are
 * visible to that tutor automatically and never show up in this list.
 */
export default function TutorPaperAccessRequests() {
  const [status, setStatus] = useState("pending");
  const [paperType, setPaperType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  // Review dialog: { ids, status } for approve/reject, or { revokeId } for revoke.
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const [grantOpen, setGrantOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/tutor-paper-access", {
        params: {
          status,
          paper_type: paperType || undefined,
          search: search || undefined,
          page: page + 1,
          per_page: perPage,
        },
      });
      const data = res.data?.data;
      setRows(data?.data || []);
      setTotal(data?.total || 0);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load tutor access requests.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [status, paperType, search, page, perPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setSelected([]);
  }, [status, paperType, search, page, perPage]);

  const selectableIds = rows.filter((r) => r.status === "pending").map((r) => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

  const toggleAll = () => setSelected(allSelected ? [] : selectableIds);
  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const openReview = (action) => {
    setReviewNote("");
    setReviewAction(action);
  };

  const submitReview = async () => {
    setSaving(true);
    try {
      const res = reviewAction.revokeId
        ? await api.post(`admin/tutor-paper-access/${reviewAction.revokeId}/revoke`, {
            review_note: reviewNote.trim() || null,
          })
        : await api.post("admin/tutor-paper-access/review", {
            ids: reviewAction.ids,
            status: reviewAction.status,
            review_note: reviewNote.trim() || null,
          });
      toast.success(res.data?.message || "Saved.");
      setReviewAction(null);
      setSelected([]);
      fetchRows();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const reviewTitle = reviewAction?.revokeId
    ? "Revoke access"
    : reviewAction?.status === "approved"
      ? `Approve ${reviewAction?.ids.length} request(s)`
      : `Reject ${reviewAction?.ids?.length} request(s)`;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Tutor Access Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tutors ask for past paper and mock exam PDFs here. Anything already assigned to a
            tutor's students is visible to them without a request.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<LockOpenIcon />} onClick={() => setGrantOpen(true)}>
          Grant access
        </Button>
      </Stack>

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={status}
          onChange={(_, v) => {
            setStatus(v);
            setPage(0);
          }}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ p: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={paperType}
              onChange={(e) => {
                setPaperType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All types</MenuItem>
              <MenuItem value="past_paper">Past papers</MenuItem>
              <MenuItem value="mock_exam">Mock exams</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search tutor or paper"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput.trim());
                setPage(0);
              }
            }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setSearch(searchInput.trim());
              setPage(0);
            }}
          >
            Search
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          {selected.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{selected.length} selected</Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => openReview({ ids: selected, status: "approved" })}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => openReview({ ids: selected, status: "rejected" })}
              >
                Reject
              </Button>
            </Stack>
          )}
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selected.length > 0 && !allSelected}
                    disabled={selectableIds.length === 0}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>Tutor</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Group / Course</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reviewed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No requests.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const chip = STATUS_CHIP[row.status] || { label: row.status };
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(row.id)}
                          disabled={row.status !== "pending"}
                          onChange={() => toggleOne(row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.tutor?.name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.tutor?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.paper_name}
                        <Typography variant="caption" display="block" color="text.secondary">
                          ID {row.paper_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{TYPE_LABELS[row.paper_type] || row.paper_type}</TableCell>
                      <TableCell>
                        {row.group?.name || "—"}
                        {row.course?.name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.course.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={row.note || ""}>
                          {row.note || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(row.requested_at)}</TableCell>
                      <TableCell>
                        <Tooltip title={row.review_note || ""} arrow>
                          <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.reviewer ? (
                          <>
                            {row.reviewer}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDate(row.reviewed_at)}
                            </Typography>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.status === "pending" && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              color="success"
                              onClick={() => openReview({ ids: [row.id], status: "approved" })}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => openReview({ ids: [row.id], status: "rejected" })}
                            >
                              Reject
                            </Button>
                          </Stack>
                        )}
                        {row.status === "approved" && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<BlockIcon />}
                            onClick={() => openReview({ revokeId: row.id })}
                          >
                            Revoke
                          </Button>
                        )}
                        {row.status === "rejected" && (
                          <Button
                            size="small"
                            color="success"
                            onClick={() => openReview({ ids: [row.id], status: "approved" })}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[15, 30, 50, 100]}
        />
      </Paper>

      <CommonModal open={Boolean(reviewAction)} onClose={() => setReviewAction(null)} width={500}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {reviewTitle}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Note to the tutor (optional)"
          value={reviewNote}
          onChange={(e) => setReviewNote(e.target.value)}
          sx={{ mb: 3 }}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button color="inherit" onClick={() => setReviewAction(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewAction?.status === "approved" ? "success" : "error"}
            disabled={saving}
            onClick={submitReview}
          >
            Confirm
          </Button>
        </Stack>
      </CommonModal>

      <GrantAccessDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onGranted={() => {
          setGrantOpen(false);
          fetchRows();
        }}
      />
    </Box>
  );
}

function useDebouncedSearch(open, loader, deps) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await loader(input.trim());
        if (!cancelled) setOptions(list);
      } catch (_) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, input, ...deps]);

  return { input, setInput, options, loading };
}

function GrantAccessDialog({ open, onClose, onGranted }) {
  const [tutor, setTutor] = useState(null);
  const [paperType, setPaperType] = useState("past_paper");
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setTutor(null);
      setItems([]);
      setNote("");
      setPaperType("past_paper");
    }
  }, [open]);

  useEffect(() => {
    setItems([]);
  }, [paperType]);

  const tutorSearch = useDebouncedSearch(
    open,
    async (search) => {
      const res = await api.get("admin/tutor-paper-access/tutors", { params: { search } });
      return res.data?.data || [];
    },
    []
  );

  const itemSearch = useDebouncedSearch(
    open,
    async (search) => {
      const endpoint = paperType === "past_paper" ? "admin/paper" : "admin/mock-exam";
      const res = await api.get(endpoint, { params: { search, per_page: 50, page: 1 } });
      const data = res.data?.data;
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      // Only approved items can be granted; the API rejects the rest.
      return list.filter((item) => Number(item.status) === 2);
    },
    [paperType]
  );

  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.post("admin/tutor-paper-access/grant", {
        tutor_id: tutor.id,
        paper_type: paperType,
        paper_ids: items.map((i) => i.id),
        review_note: note.trim() || null,
      });
      toast.success(res.data?.message || "Access granted.");
      onGranted();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to grant access.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal open={open} onClose={onClose} width={600}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
        Grant tutor access
      </Typography>

      <Autocomplete
        value={tutor}
        onChange={(_, v) => setTutor(v)}
        inputValue={tutorSearch.input}
        onInputChange={(_, v) => tutorSearch.setInput(v)}
        options={tutorSearch.options}
        loading={tutorSearch.loading}
        filterOptions={(x) => x}
        getOptionLabel={(o) => (o ? `${o.name} (${o.email})` : "")}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => <TextField {...params} label="Tutor" required />}
        sx={{ mb: 3 }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Type</InputLabel>
        <Select label="Type" value={paperType} onChange={(e) => setPaperType(e.target.value)}>
          <MenuItem value="past_paper">Past papers</MenuItem>
          <MenuItem value="mock_exam">Mock exams</MenuItem>
        </Select>
      </FormControl>

      <Autocomplete
        multiple
        value={items}
        onChange={(_, v) => setItems(v)}
        inputValue={itemSearch.input}
        onInputChange={(_, v, reason) => {
          if (reason !== "reset") itemSearch.setInput(v);
        }}
        options={itemSearch.options}
        loading={itemSearch.loading}
        filterOptions={(x) => x}
        disableCloseOnSelect
        getOptionLabel={(o) => o?.name || ""}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={paperType === "past_paper" ? "Past papers" : "Mock exams"}
            placeholder="Search by name"
            required
          />
        )}
        sx={{ mb: 3 }}
      />

      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Note to the tutor (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!tutor || items.length === 0 || saving}
          onClick={submit}
        >
          Grant access
        </Button>
      </Stack>
    </CommonModal>
  );
}
